import { Badge, Modal, Space, Tooltip } from "antd"
import { CSSProperties, useState } from "react";
import { EyeOutlined, FileOutlined, HistoryOutlined, MailOutlined, RocketOutlined, SettingOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../../store/AuthContext/AuthContext";
import { useFetch } from "../../../../../hooks/useFetch/useFetch";
import { ButtonLZ } from "../../../../components/design/Buttons/ButtonLZ";
import { customRequest } from "../../../../../common/request/customRequest";
import { EditProjectEmailsModal } from "../drawers/EditProjectEmailsModal";
import { EditProjectReportConfigModal } from "../drawers/EditProjectReportConfigModal";
import { ReportEmails } from "../../../../components/design/reports/ReportEmails";
import { ReportSendStatus } from "../../../../components/design/reports/ReportSendStatus";
import { ReportType } from "../../../../components/design/reports/ReportType";
import TableLZM from "../../../../components/design/table/TableLZM";
import { ReportLogsDrawer } from "../drawers/ReportLogsDrawer";
import { PreviewReportModal } from "../drawers/PreviewReportModal";
import { SpinLZ } from "../../../../components/design/Spinner/SpinLZ";
import { EmptyStateBox } from "../../../../components/design/EmptyStateBox/EmptyStateBox";
import { ProjectCard } from "../../../../components/design/projectgroup/ProjectCard";
import { ReportPeriodDisplay } from "../reportPeriodUtils";

const emptyReportsStyle = {
	background: '#ffffff',
	border: '1px dashed #facc15',
	color: '#94a3b8',
	width: '100%',
	display: 'flex',
	boxSizing: 'border-box' as const,
};

const filledReportsStyle = {
	width: '100%',
};

export const ReportsByProject = (props: { refreshDefinitions: any }) => {
	
	const [reportConfigWithScheduledReports, setReportConfigWithScheduledReports] = useState<any[]>([]);       
	const [updateData, setUpdateData] = useState(false);    
	const [projectEmailsModal, setProjectEmailsModal] = useState(false);
	const [projectReportsModal, setProjectReportsModal] = useState(false);
	const [showPreviewModal, setShowPreviewModal] = useState<any>(null);
	const [reportLogsDrawer, setReportLogsDrawer] = useState<any>(null);
	const { projectGroup } = useAuth();    

	const { isLoading } = useFetch(
		`${process.env.REACT_APP_API}/projects/${projectGroup.project}/reports/project`,
		{},
		[updateData, props.refreshDefinitions], 
		(data) => {        
			setReportConfigWithScheduledReports([data]);   
  
		}
	);

	const sendEmailWithReport = (emails: string[], reportType: string, schedule_type: string) => {
		Modal.confirm({
			title: '¿Confirmas el envío del informe por email?',
			okText: 'Confirmar',
			cancelText: 'Cancelar',
			onOk: async () => {
				await customRequest(
					`${process.env.REACT_APP_API}/projects/${projectGroup.project}/server-reports/type/${reportType}/mail`,
					{
						method: 'POST',
						data: { emails, schedule_type },
					}
				);
				setUpdateData(!updateData);
			},
		});
	}

	const generateReport = (reportType: string, schedule_type: string) => {
		Modal.confirm({
			title: '¿Confirmas la generación del informe?',
			okText: 'Confirmar',
			cancelText: 'Cancelar',
			onOk: async () => {
				await customRequest(
					`${process.env.REACT_APP_API}/projects/${projectGroup.project}/server-reports/type/${reportType}/preview?schedule_type=${schedule_type}`,
					{ method: 'GET' }
				);
				setUpdateData(!updateData);
			},
		});
	}

	const configLinkStyle: CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 5,
		padding: 0,
		border: 'none',
		background: 'transparent',
		color: '#8b9aab',
		fontSize: 12,
		fontWeight: 400,
		cursor: 'pointer',
	};

	const reportActionLinkStyle: CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		padding: '0px 7px',
		height: 18,
		lineHeight: '16px',
		borderRadius: 9,
		border: '1px solid #e2e8f0',
		background: '#ffffff',
		color: '#0f172a',
		fontSize: 11,
		fontWeight: 500,
		cursor: 'pointer',
	};

	const COLUMNS = [       
		{
			title: 'Proyecto',
			key: 'project',			
			width: '250px',

			render: (text: string, record: any) => (				
				<ProjectCard
					projectName={record.reportConfig?.project || projectGroup.project}
				/>
			)			
		},
		{
			title: (
				<div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 24 }}>
					<span>Destinatarios</span>
					<span style={{ paddingLeft: 20 }}>Informes</span>
				</div>
			),
			key: 'recipientsAndReports',
			onCell: () => ({ style: { verticalAlign: 'middle' } }),
			render: (record: any) => {
				const emails = record.reportConfig?.emails || [];
				const hasEmails = emails.length > 0;
				const scheduledReports = record?.scheduledReports || [];

				const recipientsBody = hasEmails ? (
					<ReportEmails emails={emails} />
				) : (
					<EmptyStateBox
						compact
						icon={<MailOutlined />}
						title="No hay emails"
						style={emptyReportsStyle}
					/>
				);

				const reportsBody = !scheduledReports.length ? (
					<EmptyStateBox
						compact
						icon={<FileOutlined />}
						title="No hay informes asignados"
						style={emptyReportsStyle}
					/>
				) : (
					<Space direction="vertical" size="middle" style={filledReportsStyle}>
						{scheduledReports.map((scheduledReport: any, index: number) => {
							const previousPeriodCovered = !!scheduledReport.previousPeriodCovered;
							const previousPeriodSent = !!scheduledReport.previousPeriodSent;
							const previousPeriodOpen = scheduledReport.previousPeriodOpen;
							const previousPeriodClose = scheduledReport.previousPeriodClose;
							const openReport = () => setShowPreviewModal({
								type: scheduledReport.type,
								schedule_type: scheduledReport.scheduled?.schedule_type,
								execute: !previousPeriodCovered,
								...(previousPeriodOpen && previousPeriodClose ? {
									openTime: previousPeriodOpen,
									closeTime: previousPeriodClose,
								} : {}),
							});

							return (
							<div
								key={index}
								style={{
									display: 'grid',
									gridTemplateColumns: '260px minmax(200px, 230px) minmax(180px, 1fr) auto',
									alignItems: 'center',
									gap: '16px',
									color: 'slategrey',
								}}
							>
								<ReportType
									reportType={scheduledReport.type}
									scope={true}
								/>
								<div style={{
									display: 'flex',
									alignItems: 'center',
									gap: 12,
									padding: '4px 10px',
									borderRadius: 8,
									border: '1px solid #cbd5e1',
									background: 'transparent',
									lineHeight: 1.2,
								}}>
									<span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
										<span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
											Periodo
										</span>
										<span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
											{scheduledReport.scheduled?.schedule_type === 'weekly' ? 'Semanal' : 'Mensual'}
										</span>
									</span>
									<span style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', minHeight: 24 }} />
									<span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
										<span style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
											Envío de emails
										</span>
										<span style={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: 5,
											fontSize: 12,
											fontWeight: 600,
											color: scheduledReport.scheduled?.enabled ? 'dodgerblue' : '#64748b',
										}}>
											<Badge status={scheduledReport.scheduled?.enabled ? 'processing' : 'default'} />
											{scheduledReport.scheduled?.enabled ? 'Automatizado' : 'Manual'}
										</span>
									</span>
								</div>
								<div style={{
									display: 'flex',
									flexDirection: 'column',
									gap: 0,
									alignItems: 'stretch',
									padding: '3px 8px',
									borderRadius: 6,
									border: '1px solid #cbd5e1',
									background: 'transparent',
									lineHeight: 1.1,
								}}>
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' }}>
										<ReportSendStatus
											compact
											generated={previousPeriodCovered}
											sent={previousPeriodSent}
											sentTo={scheduledReport.previousPeriodSentTo}
											failedTo={scheduledReport.previousPeriodFailedTo}
											total={record?.reportConfig?.emails?.length}
										/>
										{previousPeriodCovered && (
											<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
												<Tooltip title="Ver">
													<button type='button' onClick={openReport} style={reportActionLinkStyle} aria-label="Ver">
														<EyeOutlined />
													</button>
												</Tooltip>
												<Tooltip title={previousPeriodSent ? 'Ya enviado' : 'Enviar'}>
													<button
														type='button'
														disabled={previousPeriodSent}
														onClick={() => sendEmailWithReport(
															record?.reportConfig?.emails,
															scheduledReport.type,
															scheduledReport.scheduled?.schedule_type
														)}
														style={{
															...reportActionLinkStyle,
															...(previousPeriodSent ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
														}}
														aria-label="Enviar"
													>
														<MailOutlined />
													</button>
												</Tooltip>
											</span>
										)}
									</div>
									{previousPeriodOpen && previousPeriodClose && (
										<ReportPeriodDisplay
											openTime={previousPeriodOpen}
											closeTime={previousPeriodClose}
											scheduledType={scheduledReport.scheduled?.schedule_type}
											compact
											rangeOnly
										/>
									)}
								</div>
								<div style={{
									display: 'flex',
									gap: 8,
									alignItems: 'center',
									justifySelf: 'end',
									paddingLeft: 8,
									borderLeft: '1px solid #e2e8f0',
								}}>
									{previousPeriodCovered ? (
										<Tooltip title="Periodo anterior ya generado">
											<span>
												<ButtonLZ
													size='small'
													type='default'
													icon={<RocketOutlined />}
													disabled
												>
													Generar
												</ButtonLZ>
											</span>
										</Tooltip>
									) : (
										<ButtonLZ
											size='small'
											type='default'
											icon={<RocketOutlined />}
											onClick={() => generateReport(
												scheduledReport.type,
												scheduledReport.scheduled?.schedule_type
											)}
										>
											Generar
										</ButtonLZ>
									)}
									<Tooltip title="Histórico">
										<ButtonLZ
											size='small'
											onClick={() => setReportLogsDrawer({ type: scheduledReport.type })}
											type='default'
										>
											<HistoryOutlined />
										</ButtonLZ>
									</Tooltip>
								</div>
							</div>
							);
						})}
					</Space>
				);

				const columnStyle: CSSProperties = {
					display: 'flex',
					flexDirection: 'column',
					gap: 4,
					width: '100%',
				};

				const reportsColumnStyle: CSSProperties = {
					...columnStyle,
					borderLeft: '3px solid slategray',
					paddingLeft: 20,
					background: 'linear-gradient(90deg, rgba(148, 163, 184, 0.08) 0%, rgba(148, 163, 184, 0) 45%)',
					borderRadius: '0 8px 8px 0',
				};

				return (
					<div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 24, alignItems: 'center' }}>
						<div style={columnStyle}>
							{recipientsBody}
							<button
								type='button'
								onClick={() => setProjectEmailsModal(true)}
								style={configLinkStyle}
							>
								<SettingOutlined style={{ fontSize: 12 }} />
								{hasEmails ? 'Gestionar Destinatarios' : 'Configurar destinatarios'}
							</button>
						</div>
						<div style={reportsColumnStyle}>
							{reportsBody}
							<button
								type='button'
								onClick={() => setProjectReportsModal(true)}
								style={configLinkStyle}
							>
								<SettingOutlined style={{ fontSize: 12 }} />
								Asignar Informes
							</button>
						</div>
					</div>
				);
			}
		}
    ];

    return (
		<>          			  			
			{isLoading ? 
				<SpinLZ/>
				:
				<TableLZM					
				rowKey='id'
				columns={COLUMNS}
				dataSource={reportConfigWithScheduledReports}/>
			}			    
			{projectEmailsModal && reportConfigWithScheduledReports[0]?.reportConfig && (
				<EditProjectEmailsModal
					projectReportConfig={reportConfigWithScheduledReports[0].reportConfig}
					onFinish={() => { setProjectEmailsModal(false); setUpdateData(!updateData); }}
				/>
			)}
			{projectReportsModal && reportConfigWithScheduledReports[0] && (
				<EditProjectReportConfigModal
					projectReportConfig={reportConfigWithScheduledReports[0].reportConfig}
					scheduledReportList={reportConfigWithScheduledReports[0].scheduledReports}
					onFinish={() => { setProjectReportsModal(false); setUpdateData(!updateData); }}
				/>
			)}
			{showPreviewModal && (
				<PreviewReportModal
					reportInfo={showPreviewModal}
					onFinish={() => {
						const shouldRefresh = !!showPreviewModal.execute;
						setShowPreviewModal(null);
						if (shouldRefresh) setUpdateData(!updateData);
					}}
				/>
			)}			
			{reportLogsDrawer && (
				<ReportLogsDrawer
					reportInfo={reportLogsDrawer}
					onFinish={(changed) => {
						setReportLogsDrawer(null);
						if (changed) setUpdateData(!updateData);
					}}
				/>
			)}
		</>
    )	
}


